import React, { useState } from 'react';
import {
  Lock,
  ShieldAlert,
  ArrowRight,
  Package,
  Leaf,
  Eye,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Two-step portal login:
 *   1. Password gate (single shared portal password).
 *   2. Role picker (Supply Chain Manager / Sustainability Manager / External Auditor).
 *
 * One password unlocks the entire portal — main app and Optimar demo — and
 * the chosen role drives the role-based UI variations across the app.
 *
 * Session is stored in `sessionStorage` so it auto-resets when the browser
 * session ends. This is intentionally demo-grade, not enterprise auth.
 */

const DEMO_PASSWORD =
  process.env.REACT_APP_OPTIMAR_DEMO_PASSWORD || 'PURESUSTAIN-OPTIMAR-DEMO';

const ROLES = [
  {
    id: 'supply_chain_manager',
    title: 'Supply Chain Manager',
    name: 'Rim Ghebremariam',
    email: 'rim@puresustainchain.eu',
    description:
      'Full operational oversight of batches, checkpoints, and logistics flows.',
    icon: Package,
  },
  {
    id: 'sustainability_manager',
    title: 'Sustainability Manager',
    name: 'Ingrid Larson',
    email: 'ingrid.larson@puresustain.com',
    description:
      'Environmental metrics, compliance monitoring, and ESG reporting controls.',
    icon: Leaf,
  },
  {
    id: 'external_viewer',
    title: 'External Auditor',
    name: 'Lars Berg',
    email: 'lars.berg@external-audit.no',
    description: 'Read-only traceability and audit visibility.',
    icon: Eye,
  },
];

const Login = ({ onLogin }) => {
  const [step, setStep] = useState('password'); // 'password' | 'role'
  const [pwd, setPwd] = useState('');
  const [error, setError] = useState('');
  const [loadingRoleId, setLoadingRoleId] = useState(null);

  const submitPassword = (e) => {
    e.preventDefault();
    if (pwd !== DEMO_PASSWORD) {
      setError('Incorrect password. Please try again.');
      return;
    }
    setError('');
    setStep('role');
  };

  const pickRole = (role) => {
    if (loadingRoleId) return;
    setLoadingRoleId(role.id);
    const user = {
      name: role.name,
      email: role.email,
      role: role.id,
    };
    const token = 'demo-portal-token';
    toast.success(`Welcome, ${role.name}`);
    onLogin(user, token);
  };

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10"
      data-testid="login-page"
    >
      <div className="w-full max-w-3xl">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          {/* Step header */}
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-blue-50 p-2 shrink-0">
              <Lock className="h-6 w-6 text-blue-700" />
            </div>
            <div className="min-w-0 flex-1">
              <h1
                className="text-xl font-bold leading-tight text-slate-900 sm:text-2xl"
                style={{ fontFamily: 'Manrope' }}
              >
                PureSustain Platform
              </h1>
              <p className="mt-1 text-sm font-medium text-amber-700">
                {step === 'password'
                  ? 'Restricted portal access'
                  : 'Select your role to continue'}
              </p>
            </div>
            {step === 'role' && (
              <button
                type="button"
                onClick={() => {
                  setStep('password');
                  setPwd('');
                }}
                data-testid="login-back-to-password"
                className="text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                ← Back
              </button>
            )}
          </div>

          {/* Step 1 — password */}
          {step === 'password' && (
            <form
              className="mt-6 max-w-md space-y-4"
              onSubmit={submitPassword}
              data-testid="login-step-password"
            >
              <label className="block">
                <span className="block text-sm font-medium text-slate-700">
                  Portal password
                </span>
                <input
                  type="password"
                  autoComplete="off"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  data-testid="login-password-input"
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter the portal password"
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                  autoFocus
                />
              </label>

              {error && (
                <div
                  id="login-error"
                  role="alert"
                  data-testid="login-error"
                  className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
                >
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-800"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          )}

          {/* Step 2 — role picker */}
          {step === 'role' && (
            <div
              className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3"
              data-testid="login-step-role"
            >
              {ROLES.map((role) => {
                const Icon = role.icon;
                const isLoading = loadingRoleId === role.id;
                return (
                  <button
                    key={role.id}
                    type="button"
                    disabled={!!loadingRoleId}
                    onClick={() => pickRole(role)}
                    data-testid={`login-role-${role.id}`}
                    className="group flex flex-col items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-blue-400 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 sm:p-5"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="rounded-lg bg-blue-50 p-2 text-blue-700">
                        <Icon className="h-5 w-5" />
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-300 transition group-hover:text-blue-500" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className="text-base font-semibold text-slate-900"
                        style={{ fontFamily: 'Manrope' }}
                      >
                        {role.title}
                      </p>
                      <p className="mt-0.5 text-sm font-medium text-slate-700">
                        {role.name}
                      </p>
                      <p className="mt-2 text-xs leading-relaxed text-slate-500">
                        {role.description}
                      </p>
                    </div>
                    {isLoading && (
                      <p className="text-xs font-medium text-blue-700">
                        Entering portal…
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
            <strong className="font-semibold">Demo disclaimer.</strong> This
            portal includes the PureSustain main app and the Optimar-inspired
            demo (mock data only — does not represent real Optimar customer,
            plant, SCADA, or production data).
          </div>

          <p
            className="mt-4 text-[11px] leading-relaxed text-slate-500"
            data-testid="login-security-footnote"
          >
            Demo access protection only — production deployments require
            enterprise authentication, role-based access control, audit
            logging, encryption, and customer-specific security policies.
          </p>

          <p className="mt-4 text-center text-[11px] text-slate-400">
            PureSustain Platform © PureSustain AS · under VeraBlue Capital AS
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
